import { Segment } from '@dna/analytics'
import { RespondToViewport, WithProfiler } from '@dna/mixins'
import styles from '@dna/styles'
import {
  addToCart,
  getCategoryFromTags,
  getLineItemProperties,
  getMasterSku,
  igGetExperimentControl,
  minorToMajorUnit,
  removeTextBeforeCharacterAndTrim,
  renderPrice,
  splitProductName,
  t,
} from '@dna/utils'
import {
  LitElement,
  classMap,
  createRef,
  css,
  html,
  nothing,
  ref,
  repeat,
  when,
} from '@lit'

// Due to shadow DOM restrictions, we can't detect the buttons of the carousel pagination.
const CLOSEST_SELECTORS_OF_IGNORED_CLICKS = 'button, a a'
const SELECTORS_OF_IGNORED_CLICKS = '#media-carousel'

class DNAProductCard extends WithProfiler(RespondToViewport(LitElement)) {
  static properties = {
    product: {
      type: Object,
    },
    usPricing: {
      type: Boolean,
    },
    imageLoading: {
      type: String,
      attribute: 'image-loading',
    },
    showCarousel: {
      type: Boolean,
      attribute: 'show-carousel',
      reflect: true,
    },
    quickAddEnabled: {
      type: Boolean,
      attribute: 'quick-add',
    },
    quickViewEnabled: {
      type: Boolean,
      attribute: 'quick-view',
    },
    hideProductColor: {
      type: Boolean,
      attribute: 'hide-product-color',
    },
    hideBadges: {
      type: Boolean,
      attribute: 'hide-badges',
    },
    hideWishlistIcon: {
      type: Boolean,
      attribute: 'hide-wishlist-icon',
    },
    constructorSelectedId: {
      type: String,
    },
    isSearch: {
      type: Boolean,
      attribute: 'is-search',
    },
    sectionId: {
      type: String,
      attribute: 'section-id',
    },
    showColorCount: {
      type: Boolean,
      attribute: 'show-color-count',
    },
    sectionPosition: {
      type: Number,
      attribute: 'section-position',
    },
    trackingKey: {
      type: String,
      attribute: 'tracking-key',
    },
    test1136ready: {
      type: Boolean,
    },
    applyTest1136: {
      type: Boolean,
    },
    applyTest1137: {
      type: Boolean,
    },
    applyTest1205: {
      type: Boolean,
      attribute: 'apply-test-1205',
    },
  }

  get rawPrice() {
    let rawPrice

    // Data coming from Constructor
    if (this.product.prices) {
      rawPrice = this.product.prices[Shopify.country] * 100
    } else {
      // Data coming from Shopify Storefront API
      rawPrice = this.product.maxPrice
    }

    return rawPrice
  }

  get renderedPrice() {
    return renderPrice(this.rawPrice)
  }

  get renderedComparePrice() {
    let rawCompareAtPrice = false

    if (this.product.compareAtPrices) {
      if (this.product.compareAtPrices[Shopify.country]) {
        rawCompareAtPrice = this.product.compareAtPrices[Shopify.country] * 100
      }
    } else if (this.product.compareAtPrice) {
      rawCompareAtPrice = this.product.compareAtPrice
    }

    if (this.rawPrice >= rawCompareAtPrice) {
      return false
    }

    return rawCompareAtPrice ? renderPrice(rawCompareAtPrice) : false
  }

  get productMedia() {
    const MAX_IMAGES = this.applyTest1136 && this.isMobile ? 2 : 3
    const result = Object.assign([], this.product.media) // clone to avoid mutating original property

    if (this.hoverIndex !== -1 && this.hoverIndex !== 1) {
      const hoverImage = result.splice(this.hoverIndex, 1)[0]
      result.splice(1, 0, hoverImage)
    }

    const truncated = result.slice(0, MAX_IMAGES)
    return truncated
  }

  get hoverIndex() {
    return this.product.media.findIndex((image) =>
      image.alt.includes(this.hoverFragment),
    )
  }

  get showHoverImage() {
    const hasHoverImage = this.hoverIndex !== -1
    // @intelligems TECH-1205
    if (this.applyTest1205) {
      return this.productMedia.length > 1 && hasHoverImage
    }
    // END / @intelligems TECH-1205
    return this.productMedia.length > 1 && hasHoverImage && !this.isMobile
  }

  get productMediaEmpty() {
    return this.product.media.length === 0
  }

  get productUrl() {
    const constructedUrl = `${window.location.origin}${Shopify.routes.root}products/${this.product.handle}`
    return constructedUrl
  }

  get badges() {
    return this.product.metafields.rothys.badges
  }

  get primaryBadge() {
    let primaryBadge

    // Out of Stock
    const variants = this.product.variants || []
    if (variants.length) {
      const variantsAreAvailable = variants.filter(
        (variant) => variant.available === true,
      )
      if (variantsAreAvailable.length === 0) {
        return {
          text: t('products.product.out_of_stock'),
          type: 'out of stock',
        }
      }
    }
    // Best Seller
    if (
      this.product.best_selling_30_days ||
      this.product.metafields.espresso?.best_selling_30_days === 'true'
    ) {
      primaryBadge = {
        text: t('products.product.best_selling_product'),
        type: 'best seller',
      }
    }
    // New
    const launchedAt = this.product.launchedAt
      ? this.product.launchedAt
      : this.product.metafields.rothys?.launched_at
        ? this.product.metafields.rothys?.launched_at
        : this.product.published_at
    if (launchedAt) {
      const today = new Date()
      const todayInSeconds = Math.floor(today.getTime() / 1000)
      const launchDate = new Date(`${launchedAt}T00:00:00`)
      const launchDateInSeconds = Math.floor(launchDate.getTime() / 1000)
      const timeDifference = Math.abs(todayInSeconds - launchDateInSeconds)
      const secondsToDays = 60 * 60 * 24
      const timeDifferenceInDays = Math.ceil(timeDifference / secondsToDays)
      if (timeDifferenceInDays <= 14) {
        primaryBadge = {
          text: t('products.product.new_product'),
          type: 'new',
        }
      }
    }
    // Manual badges may override any automated badge except for Out of Stock
    let hasActivePromo = false
    const badges = this.badges?.filter(
      (badge) =>
        badge.type.toLowerCase().includes('primary') &&
        !(badge.text?.includes('$') && window.Shopify?.country !== 'US'),
    )
    if (badges?.length) {
      for (const badge of badges) {
        if (badge.type.toLowerCase().includes('promotion')) {
          if (badge.end_date?.length) {
            // Show promotion badge if the end date is in the future
            const currentTime = new Date()
            const promotionBadgeEndDate = new Date(badge.end_date)
            if (+currentTime < +promotionBadgeEndDate) {
              hasActivePromo = true
              primaryBadge = badge
            }
          } else {
            // Open-ended promotion
            hasActivePromo = true
            primaryBadge = badge
          }
        } else {
          if (!hasActivePromo) {
            // Other primary badges
            primaryBadge = badge
          } else {
            break
          }
        }
      }
    }
    return primaryBadge
  }

  get secondaryBadge() {
    let secondaryBadge
    const badges = this.badges?.filter(
      (badge) =>
        badge.type?.toLowerCase().includes('secondary') &&
        !(badge.text?.includes('$') && window.Shopify?.country !== 'US'),
    )

    if (badges?.length) {
      for (const badge of badges) {
        secondaryBadge = badge.text
      }
    }

    return secondaryBadge
  }

  get maximumOrderQuantity() {
    const limit = this.product.metafields.rothys.maximum_order_quantity
      ? this.product.metafields.rothys.maximum_order_quantity
      : __DNA_GLOBAL__.b2cMaxOrderQuantity
    return limit
  }

  get hideMobileCarousel() {
    return this.showCarousel && this.productMedia.length === 1 && this.isMobile
  }

  get productTitle() {
    return splitProductName(this?.product?.title)
  }

  get isSingleVariant() {
    return this.product.variants.length === 1
  }

  get isGiftCard() {
    return this?.product?.giftcard
  }

  get colorCount() {
    return this.product?.colorCountByMarket?.[Shopify.country] ?? null
  }

  $carousel = createRef()

  constructor() {
    super()
    this._setMobileBreakpoints(['mobile', 'tablet'])
    this.usPricing = Shopify.country === 'US'
    this.hideProductColor = false
    this.hideBadges = false
    this.hideWishlistIcon = false
    this.hoverFragment = 'hover |'
    this.isSearch = false
    this.sectionId = ''
    this.constructorSelectedId = this.product?.variants?.[0]?.id
    this.shouldTrackSlideChange = true
  }

  async connectedCallback() {
    super.connectedCallback()

    // @intelligems TECH-1136
    const isTECH1136Control = await igGetExperimentControl({
      staging: '11c4eaa7-c56c-482c-9191-1dd3ef6a7e23',
      production: '358dfeb6-5897-42c8-8f07-989415a1680e',
    })
    this.test1136ready = true
    if (!isTECH1136Control) {
      this.applyTest1136 = true
    } else {
      this.applyTest1136 = false
    }
    // END / @intelligems TECH-1136

    // @intelligems TECH-1137
    const isTECH1137Control = await igGetExperimentControl({
      staging: '1e951a74-92c3-4770-9501-20fe51e0119a',
      production: 'a8340b82-494b-44f9-a835-c0f22d331791',
    })
    if (!isTECH1137Control) {
      this.applyTest1137 = true
    } else {
      this.applyTest1137 = false
    }
    // END / @intelligems TECH-1137
  }

  disconnectedCallback() {
    super.disconnectedCallback()
  }

  async firstUpdated() {
    if (!this.isMobile && this.productMedia.length > 1) {
      await Promise.all([
        this.updateComplete,
        customElements.whenDefined('sl-carousel'),
      ])
      // We don't want programmatic slide changes to trigger analytics
      this.shouldTrackSlideChange = false
      this.$carousel?.value?.goToSlide(1, 'auto')
      this?.$carousel?.value?.updateComplete.then(() => {
        this.shouldTrackSlideChange = true
      })
    }
    super.firstUpdated()
  }

  async willUpdate(changedProperties) {
    if (
      changedProperties.has('product') ||
      changedProperties.has('showCarousel')
    ) {
      if (!this.isMobile && this.productMedia.length > 1) {
        // Sometimes we get here before the shoelace carousel element has been registered with the custom element registry
        // ...this makes sure that it's there before calling goToSlide on it.
        await Promise.all([
          this.updateComplete,
          customElements.whenDefined('sl-carousel'),
        ])
        // We don't want programmatic slide changes to trigger analytics
        this.shouldTrackSlideChange = false
        this.$carousel?.value?.goToSlide(1, 'auto')
        this?.$carousel?.value?.updateComplete.then(() => {
          this.shouldTrackSlideChange = true
        })
      }
    }
    return super.willUpdate(changedProperties)
  }

  _handleViewportChange(newViewport) {
    super._handleViewportChange(newViewport)
    this.requestUpdate()
  }

  async _handleAddToCart(event) {
    // Get the variant object that was clicked
    const button = event.target.closest('button')
    const variantIndex = button.getAttribute('data-index')
    const variant = this.product.variants[variantIndex]

    // check if maximum variant is already added to cart
    if (!__DNA_GLOBAL__.customerB2B) {
      const cartResult = await fetch(`${window.Shopify.routes.root}cart.js`)
      const detail = await cartResult.json()
      const itemInCart = detail.items.find(
        (item) => item.variant_id.toString() === variant.id.toString(),
      )
      if (itemInCart && itemInCart.quantity >= this.maximumOrderQuantity) {
        document.dispatchEvent(
          new CustomEvent('dna:cart:open', { bubbles: true, detail: detail }),
        )
        return
      }
    }

    const properties = getLineItemProperties(this.product, variant)

    const compareAtPrice = this.product.compareAtPrices
      ? this.product.compareAtPrices[Shopify.country]
      : this.product.compareAtPrice * this.shopifySubunitAdjustment

    if (compareAtPrice && Number(compareAtPrice) !== 0) {
      properties._compare_at_price = compareAtPrice * 100
    }

    addToCart({ id: variant.id, quantity: 1, properties })

    this._trackProductCardAddToCart(variant)
  }

  _selectConstructorVariant = ({ id }) => {
    this.constructorSelectedId = id ?? this.product?.variants?.[0]?.id
  }

  _trackProductCardClicked = (event) => {
    // Since the whole card is clickable, there are certain elements that we need to disable the click + navigation action for.
    if (
      event.target.closest(CLOSEST_SELECTORS_OF_IGNORED_CLICKS) ||
      event.target.matches(SELECTORS_OF_IGNORED_CLICKS)
    ) {
      event.preventDefault()
      return
    }

    // @segment
    Segment.protect(() => {
      const eventName =
        this.closest('sl-carousel-item') || this.isSearch
          ? Segment.CONSTANTS.EVENTS.PRODUCT_CAROUSEL_CLICKED
          : Segment.CONSTANTS.EVENTS.PRODUCT_CLICKED

      Segment.track(eventName, {
        category: this.productTitle?.style,
        name: this.productTitle?.color,
        price: Number(minorToMajorUnit(this.rawPrice)),
        product_carousel_pod_id:
          eventName === Segment.CONSTANTS.EVENTS.PRODUCT_CAROUSEL_CLICKED &&
          this.dataset.cnstrcRecommendationsPodId?.length
            ? this.dataset.cnstrcRecommendationsPodId
            : undefined,
        product_category: this.product.tags
          ? getCategoryFromTags(this.product?.tags)
          : undefined,
        product_click_context: this.isSearch ? 'search' : undefined,
        product_id: this.product.id,
        section_page_position: this.sectionPosition,
        section_tracking_key: this.trackingKey,
        shopify_product_id: this.product.id,
        sku_short:
          getMasterSku(this.product.sku) ||
          getMasterSku(this.product.variants[0].sku),
        url:
          this.product.onlineStoreUrl ||
          `${window.location.origin}/products/${this.product.handle}`,
      })
    })
  }

  _trackProductCardAddToCart = (variant) => {
    // @segment
    Segment.protect(() => {
      Segment.track(Segment.CONSTANTS.EVENTS.PRODUCT_CTA_CLICKED, {
        category: this.productTitle?.style,
        in_stock: variant.available,
        name: this.productTitle?.color,
        price: Number(minorToMajorUnit(this.rawPrice)),
        product_category: this.product.tags
          ? getCategoryFromTags(this.product?.tags)
          : undefined,
        product_cta_context: 'product card',
        product_cta_text: 'add to cart',
        product_id: this.product.id,
        shopify_product_id: this.product.id,
        shopify_variant_id: variant.id.toString(),
        sku: variant.sku,
        sku_short: getMasterSku(this.product.sku),
        url: this.product.onlineStoreUrl,
        variant: variant.title,
      })
    })
  }

  _trackProductCardCarouselChange = (event) => {
    if (!this.shouldTrackSlideChange) {
      return
    }

    // @intelligems TECH-1136
    if (this.applyTest1136 && this.isMobile) {
      const secondSlide = event.target.querySelector(
        'sl-carousel-item[key="1"]',
      )
      const secondImage = secondSlide
        .querySelector('dna-image')
        .renderRoot.querySelector('dna-fluid-image')
        .renderRoot.querySelector('img')

      if (secondImage && secondSlide.classList.contains('--is-active')) {
        secondImage.style.opacity = 1
      } else {
        secondImage.style.opacity = 0
      }
    }
    // END / @intelligems TECH-1136

    // @segment
    Segment.protect(() => {
      Segment.track(Segment.CONSTANTS.EVENTS.PRODUCT_IMAGE_SCROLLED, {
        category: this.productTitle?.style,
        name: this.productTitle?.color,
        price: Number(minorToMajorUnit(this.rawPrice)),
        product_category: this.product.tags
          ? getCategoryFromTags(this.product?.tags)
          : undefined,
        product_id: this.product.id,
        shopify_product_id: this.product.id,
        sku_short: getMasterSku(this.product.sku),
        url: this.product.onlineStoreUrl,
      })
    })
  }

  _handleOpenQuickViewModal = () => {
    const updatedVariants = this.product.variants.map((variant) => ({
      ...variant,
      variant_title: variant.title,
    }))
    const product = {
      media: this.product.media,
      title: this.productTitle?.style,
      price: this.product.price?.[Shopify.country]
        ? this.product.price?.[Shopify.country]
        : '',
      compareAtPrice: this.product.compareAtPrices?.[Shopify.country]
        ? this.product.compareAtPrices[Shopify.country] * 100
        : '',
      color: this.productTitle?.color,
      id: this.product.id,
      url: `${window.location.origin}${Shopify.routes.root}products/${this.product.handle}`,
      addOnItemsPresent: this.product.addOnItemsPresent,
      variant_id: this.product.variants[0].id,
      handle: this.product.handle,
      tags: this.product.tags,
      available: this.product.available,
      metafields: this.product.metafields,
      sku: this.product.sku,
      onlineStoreUrl: this.product.onlineStoreUrl,
      variants: updatedVariants,
    }
    document.dispatchEvent(
      new CustomEvent(`dna:quick-view:${this.sectionId}:open`, {
        detail: { product },
      }),
    )
  }

  _renderPrimaryBadge = () => {
    return html`
      ${when(
        this.primaryBadge,
        () => html`
          <div class="badge label-s">
            ${when(
              this.primaryBadge.type.toLowerCase().includes('promotion'),
              () => html`
                <span class="bg-midnight c-white">
                  ${this.primaryBadge.text}
                </span>
              `,
              () => html`
                <span class="bg-white c-midnight">
                  ${this.primaryBadge.text}
                </span>
              `,
            )}
          </div>
        `,
        () => nothing,
      )}
    `
  }

  _renderWishlistIcon = () => {
    return html`    <swymcs-wishlist-button class="swymcs-wishlist-button" data-cnstrc-btn="add_to_wishlist">
      <Button
        id="swymcs-wishlist-button"
        class="swymcs-wishlist-button swymcs-wishlist-button-${this.product.id}"
        data-empi="${this.product.id}"
        data-epi="${this.product.variants[0].id}"
        data-du="${this.productUrl}">
        <div
          id="swymUnfilled" 
          style="height: 100%; display: flex; justify-content: center; align-items: center;"
        >
        ${
          this.isMobile
            ? html`
            <svg width="15" height="14" viewBox="0 0 14 13" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M10.0549 1.07672C8.0262 1.07672 7.0262 3.07672 7.0262 3.07672C7.0262 3.07672 6.0262 1.07672 3.99745 1.07672C2.3487 1.07672 1.04307 2.4561 1.0262 4.10203C0.991823 7.5186 3.73651 9.94828 6.74495 11.9902C6.82789 12.0466 6.92588 12.0768 7.0262 12.0768C7.12651 12.0768 7.22451 12.0466 7.30745 11.9902C10.3156 9.94828 13.0603 7.5186 13.0262 4.10203C13.0093 2.4561 11.7037 1.07672 10.0549 1.07672Z" stroke="#03143B" stroke-width="1.00189" stroke-linecap="round" stroke-linejoin="round"/>
              <path d="M10.0549 1.07672C8.0262 1.07672 7.0262 3.07672 7.0262 3.07672C7.0262 3.07672 6.0262 1.07672 3.99745 1.07672C2.3487 1.07672 1.04307 2.4561 1.0262 4.10203C0.991823 7.5186 3.73651 9.94828 6.74495 11.9902C6.82789 12.0466 6.92588 12.0768 7.0262 12.0768C7.12651 12.0768 7.22451 12.0466 7.30745 11.9902C10.3156 9.94828 13.0603 7.5186 13.0262 4.10203C13.0093 2.4561 11.7037 1.07672 10.0549 1.07672Z" stroke="black" stroke-opacity="0.2" stroke-width="1.00189" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            `
            : html`
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M16.8877 2.24158C18.157 2.24163 19.4347 2.91568 20.4082 3.78357C21.378 4.64821 22.1475 5.79686 22.3027 6.86951L22.3457 7.18982C22.7402 10.4818 21.7769 13.0836 20.0996 15.3011C18.6005 17.2831 16.5274 18.9617 14.3701 20.5648L13.4424 21.2474C13.3614 21.3067 13.1703 21.4414 12.9199 21.556C12.6843 21.6639 12.3566 21.7708 11.998 21.7504V21.7513C11.4439 21.7513 10.991 21.5322 10.7334 21.3744L10.5449 21.2474L10.5273 21.2338C5.40488 17.131 1.50293 14.2615 1.50293 8.16248L1.50879 7.90759C1.63731 5.27054 3.74638 2.65372 7.03516 2.27576L7.06641 2.27283C8.73156 2.18637 10.0672 2.81877 10.9727 3.45544C11.4014 3.75691 11.7377 4.06421 11.9756 4.30408C12.1847 4.07425 12.4786 3.78896 12.8652 3.50525C13.7323 2.86901 15.0564 2.24158 16.8877 2.24158Z" stroke="white" stroke-linecap="round" stroke-linejoin="round"/>
              <path d="M16.5417 3.2511C19.3073 3.25129 21.4699 5.56018 21.4978 8.2843L21.491 8.78723C21.2805 13.9363 17.0594 17.5768 12.7009 20.5353L12.7 20.5343C12.4928 20.675 12.2483 20.7511 11.9978 20.7511C11.7473 20.751 11.5028 20.6752 11.2957 20.5343V20.5353C6.79614 17.4814 2.44442 13.7008 2.49878 8.2843L2.50757 8.02942C2.66266 5.41757 4.77586 3.25127 7.45483 3.2511C9.15361 3.2511 10.4093 4.09125 11.2205 4.89661C11.5457 5.21952 11.8038 5.5422 11.9978 5.81262C12.1918 5.54211 12.4507 5.21971 12.7761 4.89661C13.5873 4.09125 14.843 3.2511 16.5417 3.2511Z" stroke="#03143B" stroke-linecap="round" stroke-linejoin="round"/>
              <path d="M16.5417 3.2511C19.3073 3.25129 21.4699 5.56018 21.4978 8.2843L21.491 8.78723C21.2805 13.9363 17.0594 17.5768 12.7009 20.5353L12.7 20.5343C12.4928 20.675 12.2483 20.7511 11.9978 20.7511C11.7473 20.751 11.5028 20.6752 11.2957 20.5343V20.5353C6.79614 17.4814 2.44442 13.7008 2.49878 8.2843L2.50757 8.02942C2.66266 5.41757 4.77586 3.25127 7.45483 3.2511C9.15361 3.2511 10.4093 4.09125 11.2205 4.89661C11.5457 5.21952 11.8038 5.5422 11.9978 5.81262C12.1918 5.54211 12.4507 5.21971 12.7761 4.89661C13.5873 4.09125 14.843 3.2511 16.5417 3.2511Z" stroke="black" stroke-opacity="0.2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>`
        }
        </div>
      </Button>
    </swymcs-wishlist-button>
  `
  }

  _handleHover = (event) => {
    document.dispatchEvent(
      new CustomEvent('dna:product-card:hover', {
        bubbles: true,
        detail: {
          product: this.product,
          url: this.productUrl,
        },
      }),
    )
  }

  _renderWithProductLink(content) {
    return html`<a class="media-carousel-item__link"
      @mouseenter=${this._handleHover}
      @click=${(e) => this._trackProductCardClicked(e)}
      href=${this.productUrl}
      aria-label="${t('accessibility.view_details')} ${t('accessibility.for')} ${this.productTitle.style} ${this.productTitle.color}">
        ${content()}
      </a>`
  }

  _renderCarousel = () => {
    return html`
      <sl-carousel
        id="media-carousel"
        class="${this.applyTest1136 ? 'media-carousel--test1136' : nothing}"
        ?navigation=${!this.isMobile}
        ?pagination=${this.isMobile}
        @sl-slide-change=${this._trackProductCardCarouselChange}
        ${ref(this.$carousel)}
      >
        ${repeat(
          this.productMedia,
          (image) => image.url,
          (image, index) => html`
            <sl-carousel-item key=${index}>
              <dna-image
                src=${image.url}
                alt=${removeTextBeforeCharacterAndTrim(image.alt, '|')}
                width="100%"
                loading=${this.imageLoading}
                fetch-priority=${
                  // We only need to set for the first image visible in the slider
                  index === 0 && this.imageLoading === 'eager' ? 'high' : 'auto'
                }
                sizes="(max-width: 768px) 260px, (min-width: 768px) 600px"
                height=${this.applyTest1136 && this.isMobile && index === 1 ? '100%' : nothing}
                fit=${this.applyTest1136 && this.isMobile && index === 1 ? 'cover' : nothing}
                additional-styles=${this.applyTest1136 && this.isMobile && index === 1 ? 'position: absolute; top: 0; left: 0; opacity: 0; transition: opacity .1s ease' : nothing}
              />
            </sl-carousel-item>`,
        )}
     </sl-carousel>
    `
  }

  _renderHoverImage = () => {
    if (this.showHoverImage) {
      return html`
        <div class="thumbnail__image thumbnail__image--hover">
          <dna-image
            aspect-ratio="1"
            src=${this.productMedia[1]?.url}
            alt=${removeTextBeforeCharacterAndTrim(this.productMedia[1]?.alt, '|')}
            width="100%"
            height="100%"
            fit="cover"
            loading=${this.imageLoading}
            sizes="(max-width: 768px) 260px, (min-width: 768px) 600px"
          />
        </div>
      `
    }
  }

  _renderQuickAdd = () => {
    const firstVariant = this.product.variants[0]
    const available = firstVariant?.available
    const addToBagTranslation = `+ ${t('products.product.add_to_cart')}`
    const outOfStockTranslation = t('products.product.sold_out')
    return html`
      <div 
        class="quick-add${this.applyTest1137 ? ' quick-add--under-image' : ''}" 
        ?hidden=${!this.quickAddEnabled}
        data-cnstrc-product-detail
        data-cnstrc-item-id=${this.product.id}
        data-cnstrc-item-name=${this.product.title}
        data-cnstrc-item-price=${renderPrice(this.rawPrice, {
          includeCurrencySymbol: false,
          convertToUSD: true,
        })}
        data-cnstrc-item-variation-id=${this.constructorSelectedId}
        >
        ${when(
          this.quickAddEnabled && this.isSingleVariant,
          () => html`
            <div class="quick-add-cta${this.applyTest1137 ? ' quick-add-cta--test1137' : ''}">
              <button
                class=${classMap({
                  disabled: !available,
                  uppercase: true,
                })}
                data-index="0"
                @click=${this._handleAddToCart}
                data-cnstrc-btn="add_to_cart"
                @mouseenter=${this._selectConstructorVariant}
              >
                ${available ? addToBagTranslation : outOfStockTranslation} ${when(
                  this.applyTest1137,
                  () => html`
                    ${when(
                      !!this.renderedComparePrice,
                      () => html`<s>${this.renderedComparePrice}</s>`,
                      () => nothing,
                    )}
                    <span class="${this.renderedComparePrice ? 'price--markdown' : ''}">
                      ${this.renderedPrice}
                    </span>
                  `,
                  () => nothing,
                )}
              </button>
            </div>
          `,
          () => html`
            <ul>
              ${this.product.variants.map(
                (variant, index) =>
                  html`
                    <li>
                      <button
                        ?disabled=${!variant.available}
                        class=${classMap({
                          p3: true,
                          'disabled disabled--strikethrough':
                            !variant.available,
                        })}
                        @click=${this._handleAddToCart}
                        data-index=${index}
                        data-cnstrc-btn="add_to_cart"
                        @mouseenter=${() =>
                          this._selectConstructorVariant({
                            id: variant.id,
                          })}
                      >
                        ${variant.title}
                      </button>
                    </li>
                  `,
              )}
            </ul>
          `,
        )}
      </div>
    `
  }

  _renderQuickView = () => {
    return html`
    <div class="quick-view-container">
      <div class="quick-view-cta">
        <a @click=${(e) => {
          this._handleOpenQuickViewModal(e)
        }} role="button" aria-label="${t('sections.quick_view.aria_label')}">${t('sections.quick_view.cta_label')}</a>
      </div>
    </div>
    `
  }

  _renderHeader() {
    return html`
      <div class="header">
        <div class=${classMap({
          'badge-wrapper': true,
          'badge-wrapper--short': this.isSearch,
          'badge-wrapper--test1136': this.applyTest1136,
        })}">
          ${when(
            this.primarybadge && !this.hideBadges,
            () => nothing,
            () => html`
              ${this._renderPrimaryBadge()}
            `,
          )}
        </div>
        ${when(this.hideWishlistIcon, () => nothing, this._renderWishlistIcon)}
      </div>
    `
  }

  _renderFooter() {
    const showSecondaryBadge = this.secondaryBadge && !this.hideBadges
    const showColorText = this.productTitle.color && !this.hideProductColor
    return html`
      <div class=${classMap({
        footer: true,
        'footer--us': this.usPricing,
        'footer--no-secondary-badge': !showSecondaryBadge,
        'footer--no-carousel': !this.showCarousel,
        'footer--search': this.isSearch,
        'footer--test1136': this.applyTest1136,
      })}>
        <div class="info" data-product-handle=${this.product.handle}>
          <div class="title">
            ${when(
              this.productTitle.style,
              () =>
                html`<h3 class="p3" role="presentation"><strong>${this.productTitle.style}</strong></h3>`,
              () => nothing,
            )}
            ${when(
              showColorText,
              () =>
                html`<small class="footer__color">${this.productTitle.color}</small>`,
              () => nothing,
            )}
             ${when(
               this.showColorCount && this.colorCount,
               () =>
                 html`<div class="footer__count">${this.colorCount} ${this.colorCount > 1 ? 'Colors' : 'Color'}</div>`,
               () => nothing,
             )}
          </div>
          <div
            class="price p3 
              ${this.renderedComparePrice ? 'price--markdown' : ''}
              ${this.isSearch ? 'price--narrow' : ''}"
            data-cnstrc-item-price=${renderPrice(this.rawPrice, {
              includeCurrencySymbol: false,
              convertToUSD: true,
            })}
          >
            ${when(
              !!this.renderedComparePrice,
              () => html`<s>${this.renderedComparePrice}</s>`,
              () => nothing,
            )}
            ${this.renderedPrice}
          </div>
        </div>
        ${when(
          showSecondaryBadge,
          () => html`
            <div class="badge2 p4">
              ${this.secondaryBadge}
            </div>
          `,
          () => nothing,
        )}
          ${when(
            !this.isGiftCard && this.quickViewEnabled && this.isMobile,
            this._renderQuickView,
            () => nothing,
          )}
      </div>
    `
  }

  render() {
    return this._renderWithProductLink(
      () => html`
        ${this._renderHeader()}
        <div class=${classMap({
          thumbnail: true,
          'thumbnail--short': this.isSearch,
          'thumbnail--test1137': this.applyTest1137,
        })}>
          <!-- @intelligems TECH-1205 -->
          ${when(
            this.applyTest1205 && this.showHoverImage,
            () => html`${this._renderHoverImage()}`,
            () => nothing,
          )}
          <!-- end @intelligems TECH-1205 -->
          <div
            class="${classMap({
              'thumbnail__image thumbnail__image--featured': true,
              'thumbnail__image--featured-hover': this.showHoverImage,
              'thumbnail__image--mobile-no-carousel':
                !this.showCarousel || this.hideMobileCarousel,
            })}"
            style=${this.applyTest1205 && !this.showHoverImage ? 'opacity: 1;' : ''}
          >
            <dna-image
              src=${this.productMedia[0]?.url}
              alt=${removeTextBeforeCharacterAndTrim(this.productMedia[0]?.alt, '|')}
              width="100%"
              height="100%"
              loading=${this.imageLoading}
              sizes="(max-width: 768px) 260px, (min-width: 768px) 600px"
            />
          </div>
          <!-- @intelligems TECH-1205 -->
          ${when(
            this.applyTest1205,
            () => nothing,
            () => html`${this._renderHoverImage()}`,
          )}
          <!-- end @intelligems TECH-1205 -->
          ${when(
            this.showCarousel && this.productMedia.length > 1 && this.isMobile,
            this._renderCarousel,
            () => nothing,
          )}
          ${when(
            !this.isGiftCard &&
              this.quickAddEnabled &&
              !this.isMobile &&
              !this.applyTest1137,
            this._renderQuickAdd,
            () => nothing,
          )}
          ${when(
            !this.isGiftCard && this.quickViewEnabled && !this.isMobile,
            this._renderQuickView,
            () => nothing,
          )}
        </div>
        ${when(
          !this.isGiftCard &&
            this.quickAddEnabled &&
            !this.isMobile &&
            this.applyTest1137,
          () => this._renderQuickAdd(),
          () => nothing,
        )}
        ${this._renderFooter()}
      `,
    )
  }

  static styles = [
    styles.color,
    styles.flex,
    styles.typography,
    css`
      :host {
        background-color: var(--dna-color-sandstone);
        color: var(--dna-color-midnight);
        border: 1px solid #03143B40;
        border-top: 0;
        border-left: 0;
        display: flex;
        flex-direction: column;
        flex: 1 0 25%;
        position: relative;
        height: 100%;
        width: 100%;
        overflow: hidden;
      }
      :host([is-search="true"]) {
        display: block;
        border: 1px solid #03143B40;
      }
      h3 {
        margin: 0;
      }
      .header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        width: 100%;
      }
      .badge-wrapper {
        height: var(--dna-space-8);
        width: 100%;
      }
      .badge {
        padding: var(--dna-space-2);
        text-transform: uppercase;

        span {
          display: inline-block;
          padding: var(--dna-space-1) calc(var(--dna-space-3) / 2);
        }
      }
      .badge2 {
        border: thin solid var(--dna-color-midnight);
        display: inline-block;
        margin-top: var(--dna-space-3);
        padding: calc(var(--dna-space-5) / 4) var(--dna-space-2);
      }
      :host([is-search="true"]) .badge {
        padding: var(--dna-space-2);

        span {
          overflow-x: hidden;
          white-space: nowrap;
          max-width: 66%;
          text-overflow: ellipsis;
        }
      }
      .price {
        display: flex;
        justify-content: flex-end;
        justify-content: left;
        gap: var(--dna-space-2);
      }
      .price--markdown {
        color: var(--dna-utility-attention);
      }
      .price--markdown s {
        color: var(--dna-color-midnight);
      }
      .thumbnail {
        height: 100%;
        width: 100%;
        position: relative;
        transition: opacity 0.5s ease;
      }
      .card-image-link {
        display: block;
        height: 100%;
      }
      .thumbnail__image {
        display: block;
        opacity: 1;
        transition: opacity .5s ease;
        width: 100%;
      }
      :host([show-carousel]) .thumbnail__image {
        display: none;
      }
      :host([show-carousel]) .thumbnail__image--mobile-no-carousel {
        display: block;
      }
      .thumbnail__image--hover {
        height: 100%;
        opacity: 0;
        pointer-events: none;
        position: absolute;
        top: 0;
      }
      /* @intelligems TECH-1205 */
      :host([apply-test-1205]) .thumbnail__image--hover {
        opacity: 1;
      }
      /* END // @intelligems TECH-1205 */
      .thumbnail__image--hover a {
        pointer-events: all;
      }
      @media screen and (min-width: 1025px) {
        .thumbnail__image--featured {
          margin-top: calc(var(--dna-space-10) + var(--dna-space-5));
        } 
        :host(:focus-visible) .thumbnail__image--featured,
        :host(:hover) .thumbnail__image--featured.thumbnail__image--featured-hover {
          opacity: 0;
          pointer-events: none;
        }
        :host(:focus-visible) .thumbnail__image--hover,
        :host(:hover) .thumbnail__image--hover {
          opacity: 1;
        }
        /* @intelligems TECH-1205 */
        :host([apply-test-1205]:focus-visible) .thumbnail__image--featured,
        :host([apply-test-1205]:hover) .thumbnail__image--featured.thumbnail__image--featured-hover {
          opacity: 1;
          pointer-events: none;
        }
        :host([apply-test-1205]:focus-visible) .thumbnail__image--hover,
        :host([apply-test-1205]:hover) .thumbnail__image--hover {
          opacity: 0;
        }
        /* END // @intelligems TECH-1205 */
      }
      #media-carousel {
        --aspect-ratio: 1;
        transition: opacity .5s ease;
        --slide-gap: 0px;
      }
      #media-carousel::part(base) {
        gap: 0;
      }
      #media-carousel::part(navigation-button) {
        position: absolute;
        color: var(--dna-color-midnight);
        border-radius: 100%;
        background: #F4F2EF66;
      }
      #media-carousel::part(navigation-button--previous) {
        left: 0;
        padding: var(--dna-space-1);
        margin-left: var(--dna-space-1);
        transform: scale(0.5);
        transition: transform 0.4s ease;
      }
      :host(:hover) #media-carousel::part(navigation-button--previous) {
        transform: scale(1);
      }
      #media-carousel::part(navigation-button--next) {
        right: 0;
        padding: var(--dna-space-1);
        margin-right: var(--dna-space-1);
        transform: scale(0.5);
        transition: transform 0.4s ease;
      }
      :host(:hover) #media-carousel::part(navigation-button--next) {
        transform: scale(1);
      }
      #media-carousel::part(pagination) {
        justify-content: left;
        margin-bottom: 0;
        margin-left: 10px;
        position: absolute;
        top: 10px;
        pointer-events: none;
        gap: var(--dna-space-2);
      }
      #media-carousel::part(pagination-item) {
        background-color: transparent;
        border: 0.5px solid var(--dna-color-midnight);
        width: 5px;
        height: 5px;
        box-shadow: 0 0 0 1px #F4F2EF;
      }
      #media-carousel::part(pagination-item--active) {
        background-color: var(--dna-color-midnight);
      }
      #media-carousel::part(scroll-container) {
        border-radius: 0;
      }
      .media-carousel-item__link {
        display: flex;
        flex-direction: column;
        width: 100%;
        height: 100%;
        color: inherit;
        text-decoration: inherit;
      }
      status-favorite-button {
        margin-right: var(--dna-space-3);
        margin-top: var(--dna-space-3);
        --icon-stroke-filled: var(--dna-color-midnight);
        --icon-fill-filled: var(--dna-color-midnight);
        --icon-background: transparent;
        --icon-shadow-color: transparent;
        --icon-size: 16px;
        position: absolute;
        top: 0;
        right: 0;
        padding-left: 10px;
        padding-bottom: 10px;
        z-index: var(--dna-z-index-content);
        visibility: hidden;
      }
      @media screen and (max-width: 1024px) {
        .badge-wrapper--test1136 {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          z-index: 1;
        }
        #media-carousel.media-carousel--test1136 {
          --aspect-ratio: unset;
        }
        #media-carousel.media-carousel--test1136 sl-carousel-item {
          position: relative;
          height: 100%;
        }
        #media-carousel.media-carousel--test1136 sl-carousel-item[key="0"] {
          padding-top: var(--dna-space-8);
        }
        .media-carousel--test1136::part(pagination) {
          flex-wrap: nowrap !important;
          gap: 0 !important;
          padding: 0 var(--dna-space-2);
          margin-left: 0 !important;
          top: 0 !important;
          width: 100%;
        }
        .media-carousel--test1136::part(pagination-item) {
          border: none !important;
          border-radius: 0 !important;
          box-shadow: unset !important;
          width: 50% !important;
          height: 1px !important;
        }
        .media-carousel--test1136::part(pagination-item--active) {
          transform: unset !important;
        }
        status-favorite-button {
          margin-right: 0;
          margin-top: 0;
        }
        status-favorite-button::part(button) {
          padding-left: 5px;
          padding-bottom: 5px;
        }
      }
      status-favorite-button::part(button) {
        padding-top: var(--dna-space-4);
        padding-right: var(--dna-space-4);
      }
      @media screen and (max-width: 1024px) {
        status-favorite-button::part(button) {
          padding-top: var(--dna-space-2);
          padding-right: var(--dna-space-2);
        }
      }
      status-favorite-button::part(icon-wrapper) {
        width: unset;
        height: unset;
      }
      status-favorite-button.is-active {
        visibility: visible;
      }
      .quick-add {
        display: none;
        position: absolute;
        bottom: 0;
        width: 100%;
        max-height: 0;
        overflow: hidden;
        transition: max-height 1s ease;
        background-color: rgba(244, 242, 239, 0.5);
        color: var(--dna-color-midnight);
        font: var(--dna-body-p3-font);
        letter-spacing: 2px;
        z-index: 10;
      }
      :host(:hover) .quick-add {
        max-height: 200px;
      }
      .quick-add--under-image {
        background-color: var(--dna-color-sandstone);
      }
      .quick-view-cta {
        font: var(--dna-body-p3-font);
        font-size: 11px;
        text-transform: uppercase;
        text-decoration: underline;
        letter-spacing: 2px;
        margin-top: 16px;
      }
      @media screen and (min-width: 1025px) {
        .quick-view-cta {
          position: absolute;
          bottom: 0;
          width: 100%;
          height: 0;
          overflow: hidden;
          transition: height 0.3s ease;
          background-color: transparent;
          color: var(--dna-color-midnight);
          z-index: 10;
          text-align: center;
          align-items: center;
          margin-top: 14px;
          justify-content: center;
          font-weight: 500;
          background-color: rgba(244, 242, 239, 0.5);
        }
        .quick-view-container {
          margin-top: 0;
        }
        :host(:hover) .quick-view-cta {
          height: 46px;
          display: flex;
        }
      }
      .quick-view-cta a {
        cursor: pointer;
      }
      .quick-add-cta {
        justify-content: center;
        padding: var(--dna-space-4) 30px;
        text-align: center;
      }
      .quick-add-cta button {
        letter-spacing: 2px;
      }
      .quick-add-cta--test1137 {
        padding-bottom: var(--dna-space-6);
      }
      .quick-add ul {
        list-style-type: none;
        display: flex;
        padding-left: 30px;
        padding-right: 30px;
        margin-top: 16px;
        margin-bottom: 0px;
        flex-flow: wrap;
        justify-content: center;
      }
      .quick-add li:not(:last-child) {
        padding-right: 6px;
        padding-bottom: 16px;
      }
      .quick-add button {
        background-color: transparent;
        border: none;
        cursor: pointer;
        color: var(--dna-color-midnight);
      }
      .quick-add button.disabled {
        pointer-events: none;
      }
      .quick-add button.disabled--strikethrough {
        text-decoration: line-through;
        color: rgba(3, 20, 59, 0.5);
      }
      .footer {
        background-color: var(--dna-color-sandstone);
        flex: 1 1 auto;
        padding: var(--dna-space-8) var(--dna-space-2) var(--dna-space-2) var(--dna-space-2);
      }
      .footer--no-carousel,
      .footer--test1136 {
        padding: var(--dna-space-2);
      }
      .footer__color {
        font: var(--dna-body-p3-font);
      }
      .footer__count {
        font: var(--dna-body-p3-font);
      }
      @media screen and (max-width: 1024px) {
        .footer {
          height: 100%;
          display: flex;
          flex-direction: column;
          align-items: flex-start;
          justify-content: space-between;
        }
      }
      /* breakpoint-small desktop for search adjustments */
      @media screen and (min-width: 1024px) and (max-width: 1155px) {
        .footer--us.footer--search .price {
          min-width: 50px;
        }
      }
      /* breakpoint-desktop */
      @media screen and (min-width: 1024px) {
        .header {
          position: absolute;
          top: 0;
          z-index: var(--dna-z-index-content);
        }
        .badge {
          padding: var(--dna-space-4);
          z-index: var(--dna-z-index-content);
          font: var(--dna-label-s-font);

          span {
            padding: var(--dna-space-2);
          }
        }
        :host([is-search="true"]) .badge {
          padding: var(--dna-space-2);

          span {
            overflow-x: hidden;
            white-space: nowrap;
            max-width: 74%;
            text-overflow: ellipsis;
          }
        }
        .badge2 {
          font: var(--dna-body-p3-font);
        }
        .quick-add {
          display: block;
        }
        .price {
          display: flex;
          flex-direction: unset;
        }
        .badge-wrapper {
          height: 60px;
        }
        .badge-wrapper--short {
          height: 46px;
        }
        :host([is-search="true"]) .badge-wrapper {
          height: 30px;
        }
        .footer {
          padding: var(--dna-space-3) var(--dna-space-4) var(--dna-space-4) var(--dna-space-4);
        }
        .footer.footer--search {
          padding: calc(var(--dna-space-3) / 2) var(--dna-space-2) var(--dna-space-2);
        }
        .footer--us .price {
          min-width: 80px;
          text-align: right;
          justify-content: right;
        }
        .footer--us .info {
          display: flex;
          justify-content: space-between;
          gap: var(--dna-space-2);
        }
        :host([show-carousel]) .thumbnail__image {
          display: block;
        }
        .thumbnail {
          aspect-ratio: 1;
          height: unset;
        }
        .thumbnail--short {
          padding-bottom: calc(var(--dna-space-10) - calc(var(--dna-space-5) / 2));

          .thumbnail__image--featured {
            margin-top: var(--dna-space-10); 
          }
        }
        .thumbnail--test1137 {
          padding-bottom: 0;
        }
        :host.product-card__search .thumbnail {
          padding-bottom: calc(var(--dna-space-10) - calc(var(--dna-space-5) / 2));
          max-height: 224px;
        }
        #media-carousel::part(pagination) {
          margin-bottom: calc(var(--dna-space-5) / 2);
        }
        .footer .p3 {
          font: var(--dna-body-p2-font);
        }
        .footer__color {
          font: var(--dna-body-p2-font);
        }
        .footer__count {
          font: var(--dna-body-p2-font);
        }
        :host #swymcs-wishlist-button {
          width: var(--dna-space-10); 
          height: var(--dna-space-10); 
          padding-top: var(--dna-space-2);
        }
      }
      :host #swymcs-wishlist-button {
        cursor: pointer !important;
        position: absolute;
        z-index: 5;
        top: 0;
        right: 0;
        padding: var(--dna-space-2); 
        font-size: var(--dna-space-4); 
        cursor: pointer; 
        opacity: 1 !important; 
        color: #ffffff; 
        background: none; 
        border: unset; 
        display: flex; 
        justify-content: center;
        padding-top: 10px;
      }
      @media screen and (min-width: 1024px) {
        :host #swymcs-wishlist-button {
          top: calc(var(--dna-space-5) / 2);
          right: calc(var(--dna-space-5) / 2);
        }
        :host([is-search="true"]) #swymcs-wishlist-button {
          top: var(--dna-space-0-5);
          right: 0;
        }
      }
    `,
  ]
}

customElements.define('dna-product-card', DNAProductCard)
